import { EXPERIMENTS, HOMEPAGE_EXPERIMENT_KEY } from "@reflect/domain";

/**
 * Which homepage arm a visitor is shown, and how that choice is attributed.
 *
 * The experiment (`HOMEPAGE_EXPERIMENT_KEY`, `homepage_ab_2026_10`) declares
 * arms A and B, and ships B to every new visitor (#1070). The superseded
 * `homepage_ab_2026_05` keeps legacy's four arms for its stored rows. This
 * module owns the narrower question of which arms this app can *render*: A,
 * the port of legacy's arm A, and B, which is A plus two product-scene strips.
 * Legacy's own B, C and D were never ported, and a value naming C or D still
 * falls back.
 *
 * **Adding an arm is two edits:** add its label here, then register its
 * component in `VARIANT_PAGES` (`variants/registry.ts`), which is typed
 * `Record<HomepageVariant, …>` so the compiler fails until you do.
 */
export const RENDERABLE_VARIANTS = ["A", "B"] as const;

/** A homepage arm this app has a component for. */
export type HomepageVariant = (typeof RENDERABLE_VARIANTS)[number];

/**
 * Whether an untrusted value names an arm this app can render.
 *
 * Deliberately narrower than the experiment's ever-shipped variant list: a
 * sticky row or `?v=` link naming a retired arm is valid history but not
 * something we can put on screen, so it fails here and falls back.
 */
export function isHomepageVariant(value: string | undefined): value is HomepageVariant {
  return value !== undefined && (RENDERABLE_VARIANTS as readonly string[]).includes(value);
}

/**
 * Narrow an experiment's configured fallback to an arm this app can render.
 * A fallback with no component behind it would render nothing, so it yields
 * the first renderable arm instead. Exported for its unit test.
 *
 * @param configured - the experiment's `fallbackVariant`.
 */
export function renderableFallback(configured: string): HomepageVariant {
  return isHomepageVariant(configured) ? configured : RENDERABLE_VARIANTS[0];
}

/**
 * The arm shown when nothing else resolves — a retired assignment, an unknown
 * value, or an API that never answered (or has not answered yet).
 *
 * Read from the experiment's `fallbackVariant` in `@reflect/domain`, so the
 * api and this page fall back to the same arm. (A full rollback of B also
 * drops it from {@link RENDERABLE_VARIANTS}; see `homepage_ab_2026_10`.)
 * Exported for its unit test.
 */
export const FALLBACK_VARIANT: HomepageVariant = renderableFallback(
  EXPERIMENTS[HOMEPAGE_EXPERIMENT_KEY].fallbackVariant,
);

/**
 * The renderable arm a URL forces, if any.
 *
 * Reads legacy's `v` and `hv` query keys, with or without the `figma` prefix,
 * case-insensitively. Old ad links and QA bookmarks still name retired arms
 * (`?v=C`); those return `undefined`, so the visitor falls through to their
 * assignment instead of a blank page.
 *
 * @param search - a `location.search` string.
 */
export function parseVariantOverride(search: string): HomepageVariant | undefined {
  const params = new URLSearchParams(search);
  const raw = (params.get("v") ?? params.get("hv"))?.trim();
  if (!raw) return undefined;
  const variant = raw.replace(/^figma/i, "").toUpperCase();
  return isHomepageVariant(variant) ? variant : undefined;
}

/**
 * Chooses the visible homepage arm.
 *
 * A `v`/`hv` URL override is an operator preview control, so it wins over the
 * sticky server assignment. Normal visitors see their persisted arm — unless
 * that arm is not renderable, in which case they land on
 * {@link FALLBACK_VARIANT}.
 *
 * @param override - arm forced via `?v=`/`?hv=`, already validated as renderable.
 * @param assigned - arm from the sticky server assignment, untrusted.
 */
export function resolveHomepageVariant(
  override: HomepageVariant | undefined,
  assigned: string | undefined,
): HomepageVariant {
  if (override !== undefined) return override;
  return isHomepageVariant(assigned) ? assigned : FALLBACK_VARIANT;
}

/**
 * Keeps experiment attribution aligned with the arm actually rendered.
 *
 * A URL override reports `"override"` whatever the API said. A visitor holding
 * an assignment this app cannot render reports `"retired"` rather than the
 * API's `"sticky"`, so a visitor shown the fallback is not mixed in with
 * genuinely sticky traffic.
 *
 * @param override - arm forced via `?v=`/`?hv=`.
 * @param assigned - arm from the sticky server assignment, untrusted.
 * @param assignedSource - the API's own account of how it resolved.
 */
export function resolveHomepageVariantSource(
  override: HomepageVariant | undefined,
  assigned: string | undefined,
  assignedSource: string | undefined,
): string {
  if (override !== undefined) return "override";
  if (assigned !== undefined && !isHomepageVariant(assigned)) return "retired";
  return assignedSource ?? "fallback";
}
